/**
 * Sanitize HTML content to prevent XSS attacks
 * Allows only safe HTML tags like <br>
 */

/**
 * Sanitize HTML string by removing potentially dangerous content
 * @param {string} html - The HTML string to sanitize
 * @returns {string} - Sanitized HTML string
 */
export const sanitizeHTML = (html) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Create a temporary div element
  const temp = document.createElement('div');
  temp.textContent = html; // This escapes all HTML

  // Now selectively allow safe tags
  let sanitized = temp.innerHTML;

  // Replace <br> tags (they were escaped, now unescape them)
  sanitized = sanitized.replace(/&lt;br&gt;/gi, '<br>');

  return sanitized;
};

/**
 * Convert text with <br> tags to React-friendly format
 * This splits on <br> and returns an array of elements
 * @param {string} text - Text with <br> tags
 * @returns {Array} - Array of React elements
 */
export const textWithBreaks = (text) => {
  if (!text || typeof text !== 'string') {
    return [];
  }

  // Sanitize first to prevent XSS
  const sanitized = sanitizeHTML(text);

  // Split on <br> tags
  const parts = sanitized.split(/<br\s*\/?>/gi);

  // Return array with text and <br> elements
  return parts.reduce((acc, part, index) => {
    if (index > 0) {
      acc.push(<br key={`br-${index}`} />);
    }
    acc.push(part);
    return acc;
  }, []);
};

/**
 * Alternative: escape all HTML except <br> tags
 * @param {string} html - HTML string with <br> tags
 * @returns {string} - Escaped HTML with <br> preserved
 */
export const escapeHtmlExceptBreaks = (html) => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // First, protect <br> tags by replacing with a placeholder
  const placeholder = '___BR_TAG___';
  let text = html.replace(/<br\s*\/?>/gi, placeholder);

  // Escape all HTML
  const temp = document.createElement('div');
  temp.textContent = text;
  text = temp.innerHTML;

  // Restore <br> tags
  text = text.replace(new RegExp(placeholder, 'g'), '<br>');

  return text;
};

const sanitizeUtils = {
  sanitizeHTML,
  textWithBreaks,
  escapeHtmlExceptBreaks
};

export default sanitizeUtils;
